import moment from 'moment';

/**
 * Risolve un valore da un oggetto seguendo un path con dot notation.
 * Es: getByPath(obj, 'idCustomer.name') => obj.idCustomer?.name
 */
function getByPath(obj, path) {
    if (!obj || !path) return undefined;
    return path.split('.').reduce((curr, key) => curr?.[key], obj);
}

/**
 * Formatta un numero con separatore migliaia (.) e decimali (,) in formato italiano.
 * Es: formatItalianNumber(1234.56, 2) => "1.234,56"
 */
export function formatItalianNumber(num, decimals) {
    const fixed = parseFloat(num).toFixed(decimals);
    const [intPart, decPart] = fixed.split('.');
    const withThousands = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return decPart ? withThousands + ',' + decPart : withThousands;
}

/**
 * Formatta un valore in base al tipo di formato specificato.
 * - 'date'         => DD/MM/YYYY
 * - 'date:FORMAT'  => formato custom moment
 * - 'decimal:N'    => toFixed(N) senza separatore migliaia
 * - 'number:N'     => N decimali con separatore migliaia italiano (1.234,56)
 * - 'currency:N'   => come number:N + ' €' alla fine (1.234,56 €)
 */
function formatValue(value, format) {
    if (value === undefined || value === null || value === '') return undefined;
    if (!format) return value;

    if (format === 'date') {
        return moment(value).format('DD/MM/YYYY');
    }
    if (format.startsWith('date:')) {
        return moment(value).format(format.substring(5));
    }
    if (format.startsWith('currency:')) {
        const decimals = parseInt(format.substring(9));
        return formatItalianNumber(value, decimals) + ' €';
    }
    if (format.startsWith('number:')) {
        const decimals = parseInt(format.substring(7));
        return formatItalianNumber(value, decimals);
    }
    if (format.startsWith('decimal:')) {
        const decimals = parseInt(format.substring(8));
        return parseFloat(value).toFixed(decimals);
    }
    return value;
}

/**
 * Risolve un singolo tag dalla configurazione.
 *
 * Formati supportati:
 *   - stringa:  'path.to.value'                         => getByPath + default ''
 *   - oggetto con path:   { path: 'x.y', default: '-', format: 'date' }
 *   - oggetto con paths:  { paths: ['x.y', 'a.b'], default: '-' }   => primo non-null
 *   - oggetto con compute: { compute: (data) => ... }   => funzione custom
 */
function resolveTag(data, tagDef, parentData) {
    if (typeof tagDef === 'string') {
        const val = getByPath(data, tagDef);
        return val !== undefined && val !== null ? String(val) : '';
    }

    if (typeof tagDef === 'function') {
        return tagDef(data, parentData);
    }

    if (typeof tagDef === 'object' && tagDef !== null) {
        const defaultVal = tagDef.default !== undefined ? tagDef.default : '';

        if (tagDef.compute) {
            const val = tagDef.compute(data, parentData);
            return val !== undefined && val !== null ? val : defaultVal;
        }

        if (tagDef.paths) {
            for (const p of tagDef.paths) {
                const val = getByPath(data, p);
                if (val !== undefined && val !== null && val !== '') {
                    return formatValue(val, tagDef.format) ?? defaultVal;
                }
            }
            return defaultVal;
        }

        if (tagDef.path) {
            const val = getByPath(data, tagDef.path);
            if (val !== undefined && val !== null && val !== '') {
                return formatValue(val, tagDef.format) ?? defaultVal;
            }
            return defaultVal;
        }
    }

    return '';
}

/**
 * Risolve una configurazione di tag completa.
 *
 * @param {object} config - Configurazione tag dell'entità
 *   config.tags:     { tagName: tagDef, ... }           Tag di testata
 *   config.rows:     { source: 'rows', tags: {...} }    Tag delle righe (loop docxtemplater)
 *   config.computed: (data, ...extra) => ({...})        Tag calcolati con logica complessa
 *
 * @param {object} data - Dati dell'entità principale
 * @param {...any} extraArgs - Argomenti extra passati a computed()
 * @returns {object} Oggetto piatto con tutti i tag risolti, pronto per docxtemplater
 */
export function resolveTagConfig(config, data, ...extraArgs) {
    const result = {};

    // Risolvi tag di testata
    if (config.tags) {
        for (const [tagName, tagDef] of Object.entries(config.tags)) {
            result[tagName] = resolveTag(data, tagDef);
        }
    }

    // Risolvi righe
    if (config.rows) {
        const sourceArray = getByPath(data, config.rows.source) || [];
        result[config.rows.as || 'rows'] = sourceArray.map((row, index) => {
            const rowResult = {};
            for (const [tagName, tagDef] of Object.entries(config.rows.tags)) {
                if (tagDef === '_index') {
                    rowResult[tagName] = index + 1;
                } else {
                    rowResult[tagName] = resolveTag(row, tagDef, data);
                }
            }
            return rowResult;
        });
    }

    // Risolvi tag calcolati (possono sovrascrivere o aggiungere)
    if (config.computed) {
        const computed = config.computed(data, ...extraArgs);
        Object.assign(result, computed);
    }

    console.log('[TemplateEngine] Dati sorgente:', data);
    console.log('[TemplateEngine] Tag risolti:', result);

    return result;
}
